import { registerApplication, start } from 'single-spa';
import {
  constructApplications,
  constructLayoutEngine,
} from 'single-spa-layout';

// If we don't use relative imports in the root config and all the other
// imported modules, we would need to publish a separate system.js module for
// the config package, which would make all brands very obvious to someone
// inspecting the import-maps. To prevent that we use relative imports here, so
// it gets bundled as part of the root config.
import {
  getInstance,
  MicroFrontendLoader,
  MondrianInstance,
  /* eslint-disable-next-line import/no-relative-packages */
} from '../../config';

import {
  constructSingleSpaRoutes,
  getEnvironment,
  addErrorHandler,
  loadMicroFrontendImportMap,
  loadNotFoundApplication,
  legacyRedirect,
  loaders,
} from './utils';

const instance = getInstance(process.env.INSTANCE_NAME, getEnvironment());
if (!instance) {
  throw new Error('CRITICAL: Orchestrator instance not configured!');
}

const didRedirect = legacyRedirect(instance);

if (didRedirect) {
  document.body.innerHTML = String(loaders[MicroFrontendLoader.GENERIC]);
} else {
  loadMicroFrontendImportMap(instance);
  addErrorHandler(instance);

  const routes = constructSingleSpaRoutes(instance);

  const applications = constructApplications({
    routes,
    loadApp({ name }) {
      if (name === '@iu/myiu.fe.app.404') {
        return loadNotFoundApplication();
      }
      return System.import(name);
    },
  });

  type Application = ReturnType<typeof constructApplications>['0'];

  const { core, rest } = applications.reduce<{
    core: Application;
    rest: Application[];
  }>(
    (acc, curr) => {
      if (curr.name === '@iu/myiu.fe.app.core') {
        acc.core = curr;
      } else {
        acc.rest.push(curr);
      }
      return acc;
    },
    { core: undefined, rest: [] }
  );

  // Only register core at first
  registerApplication<{ instance: MondrianInstance }>({
    ...core,
    customProps: {
      instance,
    },
  });

  const layoutEngine = constructLayoutEngine({ routes, applications });
  layoutEngine.activate();

  start();

  // Actually wait for core to mount, then register the rest
  window.addEventListener('single-spa:first-mount', () => {
    rest.forEach(registerApplication);
  });
}
